import { Box, VStack, IconButton, Image, Flex, Text } from '@chakra-ui/react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import { ROUTES } from '../../constants/FrontendPathConstants';

const Seq4LifeUrl = '/images/Seq4life White logo.png';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const [isGenomicsSubmenuOpen, setIsGenomicsSubmenuOpen] = useState(false);
  const [isTranscriptomicsSubmenuOpen, setIsTranscriptomicsSubmenuOpen] = useState(false);
  const [isGermlineSubmenuOpen, setIsGermlineSubmenuOpen] = useState(false);
  const [isSomaticMutationOpen, setIsSomaticMutationOpen] = useState(false);
  const [isGermlineMutationOpen, setIsGermlineMutationOpen] = useState(false);

  const widthPx = 280;

  useEffect(() => {
    const val = isOpen ? `${widthPx}px` : '0px';
    document.documentElement.style.setProperty('--sidebar-width', val);
  }, [isOpen]);

  /* 
   * REMOVED: handleMainLinkClick caused submenus to collapse on selection.
   * Keeping the function empty or removing its usage to keep menus open.
   */
  const handleMainLinkClick = () => {
    // Intentionally left empty to prevent menu collapse
  };

  const NavItem = ({ to, label }: { to: string; label: string }) => {
    const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
    return (
      <Link to={to} onClick={handleMainLinkClick} style={{ textDecoration: 'none', display: 'block' }}>
        <Box
          px={4}
          py={3}
          borderRadius="8px"
          cursor="pointer"
          fontSize="md"
          fontWeight="medium"
          bg={isActive ? 'rgba(79, 73, 135, 0.35)' : 'transparent'}
          borderLeft={isActive ? '3px solid' : '3px solid transparent'}
          borderLeftColor={isActive ? '#4f4987' : 'transparent'}
          _hover={{
            bg: 'rgba(255, 255, 255, 0.08)',
            transform: 'translateX(4px)',
          }}
          transition="all 0.2s ease"
          role="group"
          aria-current={isActive ? 'page' : undefined}
          sx={{ color: '#ffffff' }}
        >
          <Text as="span" sx={{ color: '#ffffff' }}>{label}</Text>
        </Box>
      </Link>
    );
  };

  const ToggleParent = ({
    label,
    open,
    onClick,
    active,
    isSubCategory = false,
  }: {
    label: string;
    open: boolean;
    onClick: () => void;
    active: boolean;
    isSubCategory?: boolean;
  }) => (
    <Box
      px={4}
      py={3}
      borderRadius="8px"
      cursor="pointer"
      fontSize={isSubCategory ? 'md' : 'md'}
      fontWeight={isSubCategory ? 'semibold' : 'bold'}
      letterSpacing={isSubCategory ? 'normal' : 'wide'}
      textTransform={isSubCategory ? 'none' : 'uppercase'}
      bg={active ? 'rgba(79, 73, 135, 0.25)' : 'transparent'}
      _hover={{
        bg: 'rgba(255, 255, 255, 0.08)',
      }}
      transition="all 0.2s ease"
      onClick={onClick}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{ color: '#ffffff' }}
    >
      <Text as="span" sx={{ color: '#ffffff' }}>{label}</Text>
      <Box
        fontSize="10px"
        opacity={0.7}
        transform={open ? 'rotate(180deg)' : 'rotate(0deg)'}
        transition="transform 0.2s ease"
      >
        ▼
      </Box>
    </Box>
  );

  return (
    <>
      {/* Main Sidebar */}
      <Box
        position="fixed"
        left={0}
        top={0}
        h="100vh"
        w={isOpen ? `${widthPx}px` : '0'}
        bg="#1a1262"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        overflow="hidden"
        zIndex={20}
        boxShadow={isOpen ? '4px 0 20px rgba(26, 18, 98, 0.15)' : 'none'}
        data-sidebar
      >
        <Flex direction="column" h="full">
          {/* Logo Section */}
          <Box pt={4} pb={2} px={4}>
            <Flex justify="space-between" align="center">
              <Link to={ROUTES.DASHBOARD} onClick={handleMainLinkClick}>
                <Image
                  src={Seq4LifeUrl}
                  alt="Seq4Life"
                  w="180px"
                  h="auto"
                  objectFit="contain"
                  _hover={{ transform: 'scale(1.02)' }}
                  transition="transform 0.2s ease"
                  cursor="pointer"
                />
              </Link>
              <IconButton
                aria-label="Close Sidebar"
                icon={<FaTimes size={18} />}
                color="white"
                size="sm"
                variant="ghost"
                onClick={onToggle}
                _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
              />
            </Flex>
          </Box>

          {/* Divider */}
          <Box mx={4} mb={4} borderBottom="1px solid" borderColor="rgba(255, 255, 255, 0.1)" />

          {/* Navigation */}
          <Box
            flex={1}
            overflowY="auto"
            overflowX="hidden"
            px={3}
            css={{
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-track': { background: 'rgba(255, 255, 255, 0.05)' },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '4px',
              },
            }}
          >
            <VStack spacing={1} align="stretch">
              {/* GENOMICS Section */}
              <ToggleParent
                label="GENOMICS"
                open={isGenomicsSubmenuOpen}
                onClick={() => {
                  setIsGenomicsSubmenuOpen((s) => {
                    const next = !s;
                    if (!next) {
                      setIsSomaticMutationOpen(false);
                      setIsGermlineMutationOpen(false);
                    }
                    return next;
                  });
                  setIsTranscriptomicsSubmenuOpen(false);
                  setIsGermlineSubmenuOpen(false);
                }}
                active={
                  location.pathname.includes('/somatic-mutation') ||
                  location.pathname.includes('/pgx-extractor') ||
                  location.pathname.includes('/germline-mutation') ||
                  location.pathname.includes('/hla-typing') ||
                  location.pathname.includes('/haplotype-caller') ||
                  location.pathname.includes('/deep-variant') ||
                  location.pathname.includes('/mutect-2') ||
                  location.pathname.includes('/somatic-variant')
                }
              />

              {isGenomicsSubmenuOpen && (
                <VStack align="stretch" pl={2} spacing={0} mt={1}>
                  <NavItem to="/pgx-extractor" label="PGx Extractor" />
                  <NavItem to="/hla-typing" label="HLA Typing" />

                  {/* Germline Mutation Submenu */}
                  <ToggleParent
                    label="Germline Mutation"
                    open={isGermlineMutationOpen}
                    onClick={() => {
                      setIsGermlineMutationOpen((s) => !s);
                      setIsSomaticMutationOpen(false);
                    }}
                    active={
                      location.pathname.includes('/germline-mutation') ||
                      location.pathname.includes('/haplotype-caller') ||
                      location.pathname.includes('/deep-variant')
                    }
                    isSubCategory
                  />

                  {isGermlineMutationOpen && (
                    <VStack align="stretch" pl={4} spacing={0}>
                      <NavItem to="/germline-mutation/haplotype-caller" label="HaplotypeCaller" />
                      <NavItem to="/germline-mutation/deep-variant" label="Google DeepVariant" />
                    </VStack>
                  )}

                  {/* Somatic Mutation Submenu */}
                  <ToggleParent
                    label="Somatic Mutation"
                    open={isSomaticMutationOpen}
                    onClick={() => {
                      setIsSomaticMutationOpen((s) => !s);
                      setIsGermlineMutationOpen(false);
                    }}
                    active={
                      location.pathname.includes('/somatic-mutation') ||
                      location.pathname.includes('/mutect-2') ||
                      location.pathname.includes('/somatic-variant')
                    }
                    isSubCategory
                  />

                  {isSomaticMutationOpen && (
                    <VStack align="stretch" pl={4} spacing={0}>
                      <NavItem to="/somatic-mutation/mutect-2" label="Mutect2" />
                      <NavItem to="/somatic-mutation/somatic-variant" label="SomaticVariant" />
                    </VStack>
                  )}
                </VStack>
              )}

              {/* TRANSCRIPTOMICS Section */}
              <ToggleParent
                label="TRANSCRIPTOMICS"
                open={isTranscriptomicsSubmenuOpen}
                onClick={() => {
                  setIsTranscriptomicsSubmenuOpen((s) => !s);
                  setIsGenomicsSubmenuOpen(false);
                  setIsGermlineSubmenuOpen(false);
                }}
                active={
                  location.pathname.includes('/bulk-rna-seq') ||
                  location.pathname.includes('/scrna-seq')
                }
              />

              {isTranscriptomicsSubmenuOpen && (
                <VStack align="stretch" pl={2} spacing={0} mt={1}>
                  <NavItem to="/bulk-rna-seq" label="Bulk RNA Seq" />
                  <NavItem to="/scrna-seq" label="scRNA Seq" />
                </VStack>
              )}

              {/* EPIGENOMICS Section */}
              <ToggleParent
                label="EPIGENOMICS"
                open={isGermlineSubmenuOpen}
                onClick={() => {
                  setIsGermlineSubmenuOpen((s) => !s);
                  setIsTranscriptomicsSubmenuOpen(false);
                  setIsGenomicsSubmenuOpen(false);
                }}
                active={
                  location.pathname.includes('/atac-seq') ||
                  location.pathname.includes('/bisulfite-seq') ||
                  location.pathname.includes('/chip-seq')
                }
              />

              {isGermlineSubmenuOpen && (
                <VStack align="stretch" pl={2} spacing={0} mt={1}>
                  <NavItem to="/atac-seq" label="ATAC Seq" />
                  <NavItem to="/bisulfite-seq" label="Bisulfite Seq" />
                  <NavItem to="/chip-seq" label="ChIP Seq" />
                </VStack>
              )}

              {/* VCF2TXT Section - Standalone */}
              <Box mt={4} mb={2} mx={4} borderTop="1px solid" borderColor="rgba(255, 255, 255, 0.15)" />
              <NavItem to="/vcf2txt" label="VCF2TXT Converter" />
            </VStack>
          </Box>

          {/* Footer area in sidebar */}
          <Box p={4} borderTop="1px solid" borderColor="rgba(255, 255, 255, 0.1)">
            <Text fontSize="xs" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} textAlign="center">
              Seq4Life Platform
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Toggle button when sidebar is closed */}
      {!isOpen && (
        <IconButton
          aria-label="Open Sidebar"
          icon={<FaBars size={20} />}
          color="white"
          position="fixed"
          top="12px"
          left="16px"
          zIndex={30}
          onClick={onToggle}
          bg="primary.500"
          borderRadius="8px"
          size="md"
          _hover={{
            bg: 'primary.600',
            transform: 'scale(1.05)',
          }}
          boxShadow="0 2px 8px rgba(26, 18, 98, 0.3)"
        />
      )}
    </>
  );
};

export default Sidebar;
